<script>
import { Group, Rect, Line, Text } from "svelte-konva";
import { viewportState } from "@app/lib/timeline/viewport.svelte.js";
import { projectState } from "@app/store/projectStore.svelte.js";
import { themeState } from "@app/store/themeStore.svelte.js";
import { getTimelinePalette } from "@app/lib/timeline/timelinePalette.js";
import tcLib from "@cclib/lib/timecode.js";

/**
 * Renders the time ruler band at the top of the timeline plus the vertical
 * grid lines that extend down through the tracks area. Decorative only
 * (`listening={false}`); horizontal pan/seek on the ruler band is handled by
 * an interactive overlay rect in Timeline.svelte.
 *
 * @typedef {Object} Props
 * @property {number} [height]      - Ruler band height in px (default 24)
 * @property {number} [bodyHeight]  - Height the grid lines should span below the ruler
 * @property {number} [width]       - Override viewport container width (defaults to viewportState)
 */

/** @type {Props} */
let { height = 24, bodyHeight = 0, width } = $props();

const NICE_INTERVALS_SEC = [0.1, 0.25, 0.5, 1, 2, 5, 10, 15, 30, 60, 120, 300, 600, 1800, 3600];
const TARGET_LABEL_SPACING_PX = 110;

function pickMajorInterval(pxPerSec) {
    if (!pxPerSec) return 1;
    const targetSec = TARGET_LABEL_SPACING_PX / pxPerSec;
    for (let i = 0; i < NICE_INTERVALS_SEC.length; i += 1) {
        if (NICE_INTERVALS_SEC[i] >= targetSec) return NICE_INTERVALS_SEC[i];
    }
    return NICE_INTERVALS_SEC[NICE_INTERVALS_SEC.length - 1];
}

function formatTimecode(timeSec) {
    const fr = projectState.frameRate || 29.97;
    const df = projectState.dropFrame ?? true;
    const incode = Number(projectState.incode) || 0;
    try {
        return tcLib.secToTc(timeSec + incode, fr, df);
    } catch {
        // Defensive fallback so a malformed frame rate cannot blank the ruler.
        const total = Math.max(0, Math.floor(timeSec));
        const h = String(Math.floor(total / 3600)).padStart(2, "0");
        const m = String(Math.floor((total % 3600) / 60)).padStart(2, "0");
        const s = String(total % 60).padStart(2, "0");
        return `${h}:${m}:${s}:00`;
    }
}

let resolvedWidth = $derived(width ?? viewportState.containerWidth);
let palette = $derived(getTimelinePalette(themeState.effective));

let ticks = $derived.by(() => {
    const w = resolvedWidth;
    const { pxPerSec, scrollX } = viewportState;
    if (!w || !pxPerSec) return { major: [], minor: [], interval: 0 };

    const majorInterval = pickMajorInterval(pxPerSec);
    const minorInterval = majorInterval / 5;
    const startSec = scrollX / pxPerSec;
    const endSec = (scrollX + w) / pxPerSec;

    const major = [];
    const minor = [];
    const firstMajor = Math.floor(startSec / majorInterval) * majorInterval;
    for (let t = firstMajor; t <= endSec + majorInterval; t += majorInterval) {
        const x = Math.round(t * pxPerSec - scrollX);
        if (x < -200 || x > w + 200) continue;
        major.push({ time: t, x, label: formatTimecode(t) });
    }

    const firstMinor = Math.floor(startSec / minorInterval) * minorInterval;
    const epsilon = minorInterval * 1e-4;
    for (let t = firstMinor; t <= endSec + minorInterval; t += minorInterval) {
        // Skip ticks that coincide with a major tick.
        const remainder = Math.abs(t - Math.round(t / majorInterval) * majorInterval);
        if (remainder < epsilon) continue;
        const x = Math.round(t * pxPerSec - scrollX);
        if (x < -200 || x > w + 200) continue;
        minor.push({ time: t, x });
    }

    return { major, minor, interval: majorInterval };
});
</script>

<Group listening={false}>
    <Rect
        x={0}
        y={0}
        width={resolvedWidth}
        height={height}
        fill={palette.rulerBg}
        listening={false}
        staticConfig={true} />

    <!-- Bottom border under ruler band -->
    <Line
        points={[0, height, resolvedWidth, height]}
        stroke={palette.rulerBorder}
        strokeWidth={1}
        listening={false}
        staticConfig={true} />

    <!-- Minor tick marks (ruler band only) -->
    {#each ticks.minor as tick (tick.time)}
        <Line
            points={[tick.x, height - 5, tick.x, height]}
            stroke={palette.rulerTickMinor}
            strokeWidth={1}
            listening={false}
            staticConfig={true} />
    {/each}

    <!-- Major tick marks (full ruler band) plus body grid line extending down -->
    {#each ticks.major as tick (tick.time)}
        <Line
            points={[tick.x, 0, tick.x, height]}
            stroke={palette.rulerTickMajor}
            strokeWidth={1}
            listening={false}
            staticConfig={true} />
        {#if bodyHeight > 0}
            <Line
                points={[tick.x, height, tick.x, height + bodyHeight]}
                stroke={palette.gridLine}
                strokeWidth={1}
                listening={false}
                staticConfig={true} />
        {/if}
        <Text
            x={tick.x + 4}
            y={4}
            text={tick.label}
            fontSize={10}
            fontFamily="monospace"
            fill={palette.rulerText}
            listening={false}
            staticConfig={true} />
    {/each}
</Group>
